import React, { useMemo } from 'react';

/**
 * Platform filter: one round toggle per detection platform, with the number of
 * proteins that platform detected, and a switch between matching any selected
 * platform (union) or all of them (intersection). A protein measured on several
 * platforms counts under each of them, so every overlap is reachable -- a
 * partition (the donut this replaced) could only show a few combinations.
 *
 * Same look and controls as the Lung Diseases buttons (DiseaseGroupFilter).
 * Rows carry `platformKeys` (see MainGrid); the filtering itself happens in
 * MainGrid's doesExternalFilterPass.
 */

export const PLATFORM_OPTIONS = [
  { key: 'MS', label: 'MS', title: 'Mass spectrometry' },
  { key: 'SomaScan', label: 'SomaScan', title: 'SomaScan aptamer assay, any version' },
  { key: 'SomaScan 7k', label: 'SomaScan 7k', title: 'SomaScan 7k assay' },
  { key: 'Olink', label: 'Olink', title: 'Olink proximity extension assay (Target 96 Cardiometabolic)' },
  { key: 'MSD', label: 'MSD', title: 'Meso Scale Discovery immunoassays' },
];

// Two-circle Venn icon for the set operations: `union` fills both circles,
// `intersection` only the lens they share. Colours follow currentColor.
function VennIcon({ kind }) {
  const id = `venn-clip-${kind}`;
  return (
    <svg className="platform-mode-venn" width="44" height="28" viewBox="0 0 44 28" aria-hidden="true">
      <defs>
        <clipPath id={id}><circle cx="16" cy="14" r="11" /></clipPath>
      </defs>
      {/* Opaque shapes under one group opacity, so the union is one even tint
          (no darker overlap) and the same tint as the intersection lens. */}
      <g opacity="0.45">
        {kind === 'union' ? (
          <>
            <circle cx="16" cy="14" r="11" fill="currentColor" />
            <circle cx="28" cy="14" r="11" fill="currentColor" />
          </>
        ) : (
          <circle cx="28" cy="14" r="11" fill="currentColor" clipPath={`url(#${id})`} />
        )}
      </g>
      <circle cx="16" cy="14" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="28" cy="14" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export function platformRowPasses(keys, selected, mode) {
  if (selected.size === 0) return true;
  const has = new Set(keys || []);
  const wanted = Array.from(selected);
  return mode === 'and' ? wanted.every(k => has.has(k)) : wanted.some(k => has.has(k));
}

export default function PlatformFilter({ rowData, selected, onToggle, mode, onModeChange, onClear }) {
  const counts = useMemo(() => {
    const c = Object.fromEntries(PLATFORM_OPTIONS.map(o => [o.key, 0]));
    for (const row of rowData || []) {
      for (const k of row.platformKeys || []) {
        if (k in c) c[k] += 1;
      }
    }
    return c;
  }, [rowData]);

  const available = PLATFORM_OPTIONS.filter(o => counts[o.key] > 0);
  // The set operation only matters once two or more platforms are selected.
  const multi = selected.size > 1;

  return (
    <div className="disease-group-filter">
      <div className="disease-group-buttons" role="group" aria-label="Detection platforms">
        {available.map(o => {
          const on = selected.has(o.key);
          return (
            <button
              key={o.key}
              type="button"
              className={`disease-group-btn${on ? ' active' : ''}`}
              aria-pressed={on}
              title={o.title}
              onClick={() => onToggle(o.key, !on)}
            >
              <span className="disease-group-dot" aria-hidden="true" />
              <span className="disease-group-label">{o.label}</span>
              <span className="disease-group-count">{counts[o.key]}</span>
            </button>
          );
        })}
      </div>

      <div className="disease-group-controls">
        {/* Two-sided switch: the set operation named on each side, the active
            one at full strength. Either side's text also selects it. */}
        <div className={`platform-mode-switch${multi ? '' : ' idle'}`}>
          <button
            type="button"
            className={`platform-mode-side left${mode === 'or' ? ' active' : ''}`}
            aria-pressed={mode === 'or'}
            onClick={() => onModeChange('or')}
          >
            <VennIcon kind="union" />
            <span className="platform-mode-text">
              <strong>Union</strong>
              <span className="disease-group-switch-hint">Proteins detected on any of the selected platforms.</span>
            </span>
          </button>
          <label className="disease-group-switch" aria-label="Combine selected platforms by intersection">
            <input
              type="checkbox"
              checked={mode === 'and'}
              onChange={e => onModeChange(e.target.checked ? 'and' : 'or')}
            />
            <span className="disease-group-switch-track" aria-hidden="true">
              <span className="disease-group-switch-knob" />
            </span>
          </label>
          <button
            type="button"
            className={`platform-mode-side right${mode === 'and' ? ' active' : ''}`}
            aria-pressed={mode === 'and'}
            onClick={() => onModeChange('and')}
          >
            <span className="platform-mode-text">
              <strong>Intersection</strong>
              <span className="disease-group-switch-hint">Only proteins detected on all of the selected platforms at once.</span>
            </span>
            <VennIcon kind="intersection" />
          </button>
        </div>

        {selected.size > 0 && (
          <button type="button" className="pill-btn" onClick={onClear}>
            Clear platform filter
          </button>
        )}
      </div>
    </div>
  );
}
